import { Component, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Home } from '@/pages/Home';
import { Products } from '@/pages/Products';
import { Contact } from '@/pages/Contact';
import { FaqPage } from '@/pages/FaqPage';
import { About } from '@/pages/About';
import { Legal } from '@/pages/Legal';
import { NotFound } from '@/pages/NotFound';
import { AdminLogin } from '@/pages/admin/AdminLogin';
import { AdminDashboard } from '@/pages/admin/AdminDashboard';
import { AdminGate } from '@/pages/admin/AdminGate';
import { initLenis, scrollTopImmediate, scrollToElement } from '@/lib/lenis';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-white p-8 text-center">
          <img src="/brand/logo.png" alt="CapableBee" className="h-10 w-auto" />
          <p className="font-bold text-brand-navy">Something went wrong.</p>
          <a href="/" className="rounded-full bg-brand-sky px-6 py-3 text-sm font-bold text-white">
            Back to Home
          </a>
        </div>
      );
    }
    return this.props.children;
  }
}

const Shell = () => {
  const { pathname, hash } = useLocation();
  const isAdmin = pathname.startsWith('/admin');

  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => scrollToElement(hash), 80);
      return () => clearTimeout(t);
    }
    scrollTopImmediate();
  }, [pathname, hash]);

  return (
    <>
      {!isAdmin && <Navbar />}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy-policy" element={<Legal page="privacy-policy" />} />
          <Route path="/terms-of-service" element={<Legal page="terms-of-service" />} />
          <Route path="/refund-policy" element={<Legal page="refund-policy" />} />
          <Route path="/cookie-policy" element={<Legal page="cookie-policy" />} />
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin/dashboard"
            element={
              <AdminGate>
                <AdminDashboard />
              </AdminGate>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {!isAdmin && <Footer />}
    </>
  );
};

function App() {
  useEffect(() => {
    initLenis();
  }, []);

  return (
    <div className="app-root">
      <BrowserRouter>
        <ErrorBoundary>
          <Shell />
          <Toaster position="bottom-right" richColors />
        </ErrorBoundary>
      </BrowserRouter>
    </div>
  );
}

export default App;
